import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Buildings, UsersThree, Briefcase, ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=2400&q=80";
const MIDDLE_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1800&q=80";
const CITY_IMG =
  "https://images.pexels.com/photos/14395955/pexels-photo-14395955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";

const icons = [Buildings, UsersThree, Briefcase];

export default function Rent() {
  const { t } = useLang();
  const titleRef = useRef(null);

  // Simple letter-by-letter fade-in for the hero title
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.classList.remove("opacity-0");
  }, []);

  return (
    <div data-testid="rent-page" className="bg-[#0F0F0F]">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end pb-24 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-[#0F0F0F]" />
        </div>

        {/* Decorative gold frame */}
        <div className="absolute top-32 left-6 md:left-12 w-24 h-24 border-t border-l border-[#D4AF37]/60 pointer-events-none" />
        <div className="absolute bottom-32 right-6 md:right-12 w-24 h-24 border-b border-r border-[#D4AF37]/60 pointer-events-none" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <div className="eyebrow mb-6 reveal">{t.rent.eyebrow}</div>
          <h1
            ref={titleRef}
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#F5F5F0] tracking-tight leading-[0.95] max-w-5xl reveal reveal-delay-1"
          >
            {t.rent.title}
          </h1>
          <span className="block gold-line mt-10 reveal reveal-delay-2"></span>
          <p className="text-white/70 mt-10 max-w-2xl leading-relaxed text-lg reveal reveal-delay-3">
            {t.rent.intro}
          </p>
        </div>
      </section>

      {/* PILLARS — Who it's for */}
      <section className="py-28 md:py-36 bg-[#0B0B0B] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-20">
            <div className="eyebrow mb-4">{t.rent.pillars_title}</div>
            <span className="gold-line"></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10">
            {t.rent.pillars.map((p, i) => {
              const Icon = icons[i % icons.length];
              return (
                <div
                  key={i}
                  className="group relative border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500 p-10 bg-[#141414]"
                  data-testid={`rent-pillar-${i}`}
                >
                  <div className="absolute top-8 right-8 text-[#D4AF37]/70 group-hover:text-[#D4AF37] transition-colors">
                    <Icon size={40} weight="thin" />
                  </div>
                  <div className="font-serif text-[#D4AF37] text-4xl mb-8">{p.n}</div>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mb-5 leading-tight pr-10">{p.t}</h3>
                  <p className="text-white/60 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDITORIAL IMAGE BREAK */}
      <section className="relative h-[60vh] overflow-hidden zoom-wrap">
        <img src={MIDDLE_IMG} alt="" className="zoom-img w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-[#0F0F0F]" />
      </section>

      {/* PROCESS STEPS */}
      <section className="py-28 md:py-36 bg-[#0F0F0F] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="mb-20 max-w-3xl">
            <div className="eyebrow mb-4">{t.rent.how_title}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] tracking-tight leading-tight">
              {t.rent.how_title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {t.rent.steps.map((s, i) => (
              <div
                key={i}
                className={`relative py-12 px-8 md:px-12 border-white/5 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } ${i < t.rent.steps.length - 2 ? "border-b md:border-b" : "border-b md:border-b-0"}`}
                data-testid={`rent-step-${i}`}
              >
                <div className="flex items-baseline gap-6 mb-5">
                  <span className="font-serif text-[#D4AF37] text-6xl leading-none">{s.n}</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] leading-tight">{s.t}</h3>
                </div>
                <p className="text-white/60 leading-relaxed pl-[4.5rem]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 md:py-44 overflow-hidden grain border-t border-white/5">
        <div className="absolute inset-0">
          <img src={CITY_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] leading-tight tracking-tight mb-10">
            {t.rent.cta_title}
          </h2>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-3" data-testid="rent-cta">
            <span>{t.rent.cta}</span>
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
